import {DataFrame} from 'data-frames/DataFrameTypes';
import {LeafType, LeafTypeToTSType} from 'data-types/benchling-type-def/BenchlingTypeDef';
import {omit} from 'util/omitKey';

import {createAccessors} from './createAccessors';
import filterRows from './filterRows';
import findAndAssertColumn from './findAndAssertColumn';
import transformColumn from './transformColumn';
import updateColumnDef from './updateColumnDef';

// If a type should be coerced by coerceOrFilterNulls instead of filtered, then specify a default value for
// that type.
const DATA_TYPE_TO_DEFAULT_VALUE: {[key in LeafType]?: LeafTypeToTSType[key]} = {
  [LeafType.BOOLEAN]: false,
  [LeafType.TEXT]: 'No value',
};

/**
 * Depending on the leaf type of the column, coerce null values or filter rows containing null values
 *
 * The following leaf types will be coerced as follows:
 *  - BOOLEAN: false
 *  - TEXT: "No value"
 *
 * All other leaf types will have their rows filtered
 *
 * Column types will also be made non-nullable by removing any `isNullable` flag on the type.
 *
 * isList and maybeText type flags will be ignored.
 *
 * Columns that are not `isNullable` will be ignored
 */
export default function coerceOrFilterNulls(dataFrame: DataFrame, columnSystemName: string): DataFrame {
  const column = findAndAssertColumn(dataFrame, columnSystemName);

  if (!column.type.isNullable) {
    return dataFrame;
  }

  const {
    accessors: {columnSystemName: accessor},
    validatedLeafTypes: {columnSystemName: columnType},
  } = createAccessors(dataFrame, {columnSystemName});

  const coerceValue = DATA_TYPE_TO_DEFAULT_VALUE[columnType];

  const dataFrameWithoutNulls =
    coerceValue != null
      ? transformColumn(dataFrame, columnSystemName, (row) =>
          accessor(row) == null ? coerceValue : accessor(row)
        )
      : filterRows(dataFrame, (row) => accessor(row) != null);

  return updateColumnDef(dataFrameWithoutNulls, columnSystemName, {
    type: omit(column.type, 'isNullable'),
  });
}
